// Copyright 2016 Sorama B.V.

import { bindable } from "aurelia-framework";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { Unit } from "units/Unit";

class TouchRecord {
    id: number;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    startViewport: Rect;
}

export class ZoomAxis {
    static None = "";
    static X = "x";
    static Y = "y";
    static XY = "xy";
}

export class ZoomPanPart extends PlotPartBase {
    //#region Constants
    //#endregion

    //#region Bindables
    @bindable public zoomAxis: ZoomAxis = ZoomAxis.XY;
    @bindable public minZoomX = 0.0001; // Default for x remains nonzero
    @bindable public minZoomY = 0; // Set to 0 to allow complete zoom out on y dimension if desired
    @bindable public zoomStep = 1.5;
    //#endregion

    private lastMovePositionX = -1;
    private lastMovePositionY = -1;
    private preventNextContextMenu = false;

    //#region Fields
    private currentTouches: TouchRecord[] = [];

    //#endregion

    //#region Constructor and Initialisation
    constructor() {
        super(0);
    }

    public attached(): void {
        super.attached();

        this.plotParent.addInputEventListener("touchstart", this.touchDown);
        this.plotParent.addWindowInputEventListener("touchmove", this.touchMove);
        this.plotParent.addWindowInputEventListener("touchend", this.touchUp);
        this.plotParent.addInputEventListener("mousedown", this.onMouseDown);
        this.plotParent.addWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.addWindowInputEventListener("mouseup", this.onMouseUp);
        this.plotParent.addInputEventListener("wheel", this.onWheel);
        this.plotParent.addWindowInputEventListener("contextmenu", this.onContextMenu);
    }

    public detached(): void {
        this.plotParent.removeInputEventListener("touchstart", this.touchDown);
        this.plotParent.removeWindowInputEventListener("touchmove", this.touchMove);
        this.plotParent.removeWindowInputEventListener("touchend", this.touchUp);
        this.plotParent.removeInputEventListener("mousedown", this.onMouseDown);
        this.plotParent.removeWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.removeWindowInputEventListener("mouseup", this.onMouseUp);
        this.plotParent.removeInputEventListener("wheel", this.onWheel);
        this.plotParent.removeWindowInputEventListener("contextmenu", this.onContextMenu);

        super.detached();
    }

    //#endregion

    //#region IPlotPart Members
    public onMeasureMargin(): Margin {
        return new Margin(0, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        super.onSize(size, margin);

        this.updateControl();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        super.onViewPortComputed(viewPort, viewPortBounds, units);

        this.updateControl();
    }

    //#endregion

    //#region User Input Methods
    private isMouseDown = false;

    private onMouseDown = (event: MouseEvent): void => {
        if (!this.isMouseDown && event.button === 2) {
            this.lastMovePositionX = event.clientX;
            this.lastMovePositionY = event.clientY;
            this.isMouseDown = true;
        } else {
            this.isMouseDown = false;
        }
    };

    private onMouseMove = (event: MouseEvent): void => {
        if ((event.buttons & 2) === 0) {
            this.isMouseDown = false;
            return;
        }

        if (this.isMouseDown) {
            const movementX = event.movementX === undefined ? event.clientX - this.lastMovePositionX : event.movementX;
            const movementY = event.movementY === undefined ? event.clientY - this.lastMovePositionY : event.movementY;
            this.lastMovePositionX = event.clientX;
            this.lastMovePositionY = event.clientY;

            let viewPortMovementX =
                this.zoomAxis === ZoomAxis.X || this.zoomAxis === ZoomAxis.XY
                    ? (-movementX / this.actualSize.width) * this.viewPort.width
                    : 0;
            let viewPortMovementY =
                this.zoomAxis === ZoomAxis.Y || this.zoomAxis === ZoomAxis.XY
                    ? (movementY / this.actualSize.height) * this.viewPort.height
                    : 0;

            // Make sure the movement does not exceed the bounds
            if (this.viewPort.x + viewPortMovementX < this.viewPortBounds.x) {
                viewPortMovementX = this.viewPortBounds.x - this.viewPort.x;
            }

            if (
                this.viewPort.x + this.viewPort.width + viewPortMovementX >
                this.viewPortBounds.x + this.viewPortBounds.width
            ) {
                viewPortMovementX =
                    this.viewPortBounds.x + this.viewPortBounds.width - (this.viewPort.x + this.viewPort.width);
            }

            if (this.viewPort.y + viewPortMovementY < this.viewPortBounds.y) {
                viewPortMovementY = this.viewPortBounds.y - this.viewPort.y;
            }

            if (
                this.viewPort.y + this.viewPort.height + viewPortMovementY >
                this.viewPortBounds.y + this.viewPortBounds.height
            ) {
                viewPortMovementY =
                    this.viewPortBounds.y + this.viewPortBounds.height - (this.viewPort.y + this.viewPort.height);
            }

            // Update viewport internally first due to possible debounce of viewport update in parent
            this.viewPort = new Rect(
                this.viewPort.x + viewPortMovementX,
                this.viewPort.y + viewPortMovementY,
                this.viewPort.width,
                this.viewPort.height
            );

            // Update parent viewport (with possible debounce)
            this.plotParent.setViewPort(this.viewPort);
        }
    };

    private onMouseUp = (event: MouseEvent): void => {
        if (this.isMouseDown && event.button === 2) {
            this.isMouseDown = false;
            this.preventNextContextMenu = true;
        }
    };

    private onWheel = (event: WheelEvent): void => {
        const inputElementRect = (<HTMLElement>event.target).getBoundingClientRect();

        // Determine the zoom direction
        const zoomDirection = -event.deltaY / Math.abs(event.deltaY);
        const zoomFactor = zoomDirection > 0 ? 1 / this.zoomStep : this.zoomStep;

        const zoomFactorX = this.zoomAxis === ZoomAxis.X || this.zoomAxis === ZoomAxis.XY ? zoomFactor : 1;
        const zoomFactorY = this.zoomAxis === ZoomAxis.Y || this.zoomAxis === ZoomAxis.XY ? zoomFactor : 1;

        // Calculate viewport sizes
        const oldViewPortSize = new Size(this.viewPort.width, this.viewPort.height);
        const newViewPortSize = new Size(oldViewPortSize.width * zoomFactorX, oldViewPortSize.height * zoomFactorY);

        // Limit size
        if (
            newViewPortSize.width < this.minZoomX * this.viewPortBounds.width &&
            (this.zoomAxis == ZoomAxis.XY || this.zoomAxis == ZoomAxis.X)
        ) {
            newViewPortSize.width = this.minZoomX * this.viewPortBounds.width;
        }
        if (
            newViewPortSize.height < this.minZoomY * this.viewPortBounds.height &&
            (this.zoomAxis == ZoomAxis.XY || this.zoomAxis == ZoomAxis.Y)
        ) {
            newViewPortSize.height = this.minZoomY * this.viewPortBounds.height;
        }

        if (newViewPortSize.width > this.viewPortBounds.width) {
            newViewPortSize.width = this.viewPortBounds.width;
        }

        if (newViewPortSize.height > this.viewPortBounds.height) {
            newViewPortSize.height = this.viewPortBounds.height;
        }

        // Pan the viewport so the position of the mouse is the center of the zoom action
        const widthChange = oldViewPortSize.width - newViewPortSize.width;
        const heightChange = oldViewPortSize.height - newViewPortSize.height;

        let newViewPortOriginX =
            this.viewPort.x + ((event.clientX - inputElementRect.left) / inputElementRect.width) * widthChange;
        let newViewPortOriginY =
            this.viewPort.y - ((event.clientY - inputElementRect.bottom) / inputElementRect.height) * heightChange; // - because svg y direction is reversed from browser y direction

        newViewPortOriginX = Math.max(newViewPortOriginX, this.viewPortBounds.x);
        newViewPortOriginY = Math.max(newViewPortOriginY, this.viewPortBounds.y);

        if (newViewPortOriginX + newViewPortSize.width > this.viewPortBounds.x + this.viewPortBounds.width) {
            newViewPortOriginX = this.viewPortBounds.x + this.viewPortBounds.width - newViewPortSize.width;
        }

        if (newViewPortOriginY + newViewPortSize.height > this.viewPortBounds.y + this.viewPortBounds.height) {
            newViewPortOriginY = this.viewPortBounds.y + this.viewPortBounds.height - newViewPortSize.height;
        }

        this.plotParent.setViewPort(
            new Rect(newViewPortOriginX, newViewPortOriginY, newViewPortSize.width, newViewPortSize.height)
        );

        event.preventDefault();
        event.stopPropagation();
    };

    private touchDown = (event: TouchEvent): boolean => {
        event.preventDefault();

        const newTouches: TouchRecord[] = [];

        // Sync
        for (let i = 0; i < event.touches.length; i++) {
            const item = this.currentTouches.find((x) => x.id == event.touches[i].identifier);

            if (item) {
                newTouches.push(item);
                continue;
            }

            const element = <HTMLElement>event.changedTouches[0].target;

            const touch: TouchRecord = {
                id: event.touches[i].identifier,
                startX: event.touches[i].pageX - element.getBoundingClientRect().left,
                currentX: event.touches[i].pageX - element.getBoundingClientRect().left,
                startViewport: this.viewPort,
                startY: event.touches[i].pageY - element.getBoundingClientRect().top,
                currentY: event.touches[i].pageY - element.getBoundingClientRect().top,
            };

            newTouches.push(touch);
        }

        this.currentTouches = newTouches;

        return true;
    };

    private touchMove = (event: TouchEvent): boolean => {
        event.preventDefault();

        // Update current entries
        for (let i = 0; i < event.changedTouches.length; i++) {
            const element = <HTMLElement>event.changedTouches[0].target;

            this.currentTouches.find((x) => x.id == event.changedTouches[i].identifier).currentX =
                event.changedTouches[i].pageX - element.getBoundingClientRect().left;
            this.currentTouches.find((x) => x.id == event.changedTouches[i].identifier).currentY =
                event.changedTouches[i].pageY - element.getBoundingClientRect().top;
        }

        if (this.currentTouches.length == 2) {
            const inputElementRect = (<HTMLElement>event.target).getBoundingClientRect();
            const oldViewport = this.currentTouches[0].startViewport.copy();

            // Determine location on original touch in pixels
            const startTopLeftX = Math.min(...this.currentTouches.map((x) => x.startX));
            const startTopLeftY = Math.min(...this.currentTouches.map((x) => x.startY));
            const startBottomLeftX = Math.max(...this.currentTouches.map((x) => x.startX));
            const startBottomLeftY = Math.max(...this.currentTouches.map((x) => x.startY));

            // Determine plot coordinates
            const x1 = oldViewport.x + (oldViewport.width * startTopLeftX) / inputElementRect.width;
            const x2 = oldViewport.x + (oldViewport.width * startBottomLeftX) / inputElementRect.width;
            const y2 = oldViewport.y + oldViewport.height * (1 - startTopLeftY / inputElementRect.height); // flip y for SVG
            const y1 = oldViewport.y + oldViewport.height * (1 - startBottomLeftY / inputElementRect.height);

            // Determine current touch coordinates in pixels
            const currentTopLeftX = Math.min(...this.currentTouches.map((x) => x.currentX));
            const currentTopLeftY = Math.min(...this.currentTouches.map((x) => x.currentY));
            const currentBottomLeftX = Math.max(...this.currentTouches.map((x) => x.currentX));
            const currentBottomLeftY = Math.max(...this.currentTouches.map((x) => x.currentY));

            // Map current coordinates to old viewport. First width & height in plot coordinates
            const newWidth = (x2 - x1) * (inputElementRect.width / (currentBottomLeftX - currentTopLeftX));
            const newHeight = (y2 - y1) * (inputElementRect.height / (currentBottomLeftY - currentTopLeftY));

            let newViewPortSize = new Rect(
                x1 - (currentTopLeftX / inputElementRect.width) * this.viewPort.width,
                y1 - (1 - currentBottomLeftY / inputElementRect.height) * this.viewPort.height, // flip y svg
                newWidth,
                newHeight
            );

            newViewPortSize = this.boundCorrectedViewPort(newViewPortSize);

            this.viewPort = newViewPortSize;
            this.plotParent.setViewPort(this.viewPort);
        }

        return true;
    };

    private touchUp = (event: TouchEvent): boolean => {
        // Delete entries
        for (let i = 0; i < event.changedTouches.length; i++) {
            const id = this.currentTouches.findIndex((x) => x.id == event.changedTouches[i].identifier);
            this.currentTouches.splice(id, 1);
        }

        return true;
    };

    private onContextMenu = (event: MouseEvent): void => {
        if (this.preventNextContextMenu) {
            event.preventDefault();
            this.preventNextContextMenu = false;
        }
    };
    //#endregion

    //#region Update and Draw Functions

    private updateControl() {}

    private boundCorrectedViewPort(newViewPortSize: Rect): Rect {
        // Limit the size and enforce the left bound to be minZoomX (default 0)
        if (newViewPortSize.x < this.minZoomX) {
            newViewPortSize.x = this.minZoomX;
        }
        // Adjust width if needed so the right boundary isn't exceeded
        if (newViewPortSize.x + newViewPortSize.width > this.viewPortBounds.x + this.viewPortBounds.width) {
            newViewPortSize.width = this.viewPortBounds.x + this.viewPortBounds.width - newViewPortSize.x;
        }

        if (newViewPortSize.y < this.viewPortBounds.y) newViewPortSize.y = this.viewPortBounds.y;
        if (newViewPortSize.y + newViewPortSize.height > this.viewPortBounds.height) {
            newViewPortSize.height = this.viewPortBounds.height - newViewPortSize.y;
        }

        if (
            newViewPortSize.height < this.minZoomY * this.viewPortBounds.height &&
            (this.zoomAxis == ZoomAxis.XY || this.zoomAxis == ZoomAxis.Y)
        ) {
            newViewPortSize.height = this.minZoomY * this.viewPortBounds.height;
        }
        return newViewPortSize;
    }

    //#endregion
}
